import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const UniversalPricePredictionPage = () => {
  const [symbol, setSymbol] = useState('');
  const [predictionHorizon, setPredictionHorizon] = useState(1);
  const [periodYears, setPeriodYears] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handlePredict = async (e) => {
    e.preventDefault();
    
    if (!symbol.trim()) {
      setError('Please enter a stock or index symbol');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const params = new URLSearchParams({
        symbol: symbol.trim(),
        prediction_horizon: predictionHorizon.toString(),
        period_years: periodYears.toString(),
      });

      const response = await api.get(`/api/v1/tools/price-prediction?${params.toString()}`);
      
      if (response.data.success) {
        setResult(response.data);
      } else {
        setError(response.data.error || 'Prediction failed');
      }
    } catch (err) {
      console.error('Error predicting price:', err);
      setError(err.response?.data?.detail || 'Failed to predict price. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    return `₹${parseFloat(price).toFixed(2)}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${parseFloat(value).toFixed(2)}%`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Universal Price Prediction</h1>
              <p className="text-gray-600">
                Predict future prices for any stock or index using machine learning
              </p>
            </div>
            <Link
              to="/tools/price-prediction-history"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              View History →
            </Link>
          </div>
        </div>

        {/* Input Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <form onSubmit={handlePredict} className="space-y-4">
            <div>
              <label htmlFor="symbol" className="block text-sm font-medium text-gray-700 mb-2">
                Stock or Index Symbol
              </label>
              <input
                type="text"
                id="symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                placeholder="e.g., RELIANCE, NIFTY, TCS, INFY"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
              <p className="mt-1 text-sm text-gray-500">
                Enter stock symbol (e.g., RELIANCE, TCS) or index name (e.g., NIFTY, SENSEX)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="horizon" className="block text-sm font-medium text-gray-700 mb-2">
                  Prediction Horizon (days)
                </label>
                <input
                  type="number"
                  id="horizon"
                  value={predictionHorizon}
                  onChange={(e) => setPredictionHorizon(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  max="30"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Number of days ahead to predict (1-30)
                </p>
              </div>

              <div>
                <label htmlFor="period" className="block text-sm font-medium text-gray-700 mb-2">
                  Historical Data Period (years)
                </label>
                <input
                  type="number"
                  id="period"
                  value={periodYears}
                  onChange={(e) => setPeriodYears(Math.max(1, Math.min(5, parseInt(e.target.value) || 2)))}
                  min="1"
                  max="5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Years of historical data to use for training (1-5)
                </p>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Predicting...' : 'Predict Price'}
            </button>
          </form>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Results Display */}
        {result && result.success && (
          <div className="space-y-6">
            {/* Main Prediction Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Prediction for {result.symbol}
              </h2>
              
              {result.prediction && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Current Price</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatPrice(result.prediction.current_price)}
                    </p>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${
                    result.prediction.prediction_direction === 'bullish' 
                      ? 'bg-green-50' 
                      : 'bg-red-50'
                  }`}>
                    <p className="text-sm text-gray-600 mb-1">Predicted Price</p>
                    <p className={`text-2xl font-bold ${
                      result.prediction.prediction_direction === 'bullish'
                        ? 'text-green-700'
                        : 'text-red-700'
                    }`}>
                      {formatPrice(result.prediction.predicted_price)}
                    </p>
                  </div>
                  
                  <div className={`rounded-lg p-4 ${
                    result.prediction.predicted_change >= 0
                      ? 'bg-green-50'
                      : 'bg-red-50'
                  }`}>
                    <p className="text-sm text-gray-600 mb-1">Predicted Change</p>
                    <p className={`text-2xl font-bold ${
                      result.prediction.predicted_change >= 0
                        ? 'text-green-700'
                        : 'text-red-700'
                    }`}>
                      {formatPercentage(result.prediction.predicted_change)}
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Direction</p>
                    <p className={`text-2xl font-bold ${
                      result.prediction.prediction_direction === 'bullish'
                        ? 'text-green-700'
                        : 'text-red-700'
                    }`}>
                      {result.prediction.prediction_direction === 'bullish' ? '📈 Bullish' : '📉 Bearish'}
                    </p>
                  </div>
                </div>
              )}

              {/* Confidence Interval */}
              {result.prediction && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Confidence Interval</h3>
                  <div className="flex items-center space-x-4">
                    <div>
                      <p className="text-sm text-gray-600">Lower Bound</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatPrice(result.prediction.confidence_interval_lower)}
                      </p>
                    </div>
                    <div className="flex-1 border-t-2 border-gray-300"></div>
                    <div>
                      <p className="text-sm text-gray-600">Upper Bound</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatPrice(result.prediction.confidence_interval_upper)}
                      </p>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Predicted price range for {result.prediction.days_ahead} day(s) ahead
                  </p>
                </div>
              )}

              {/* Model Metrics */}
              {result.training_metrics && (
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Model Performance</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Test R² Score</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {result.training_metrics.test_r2 !== undefined 
                          ? `${(result.training_metrics.test_r2 * 100).toFixed(2)}%`
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Test MAE</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {result.training_metrics.test_mae_pct !== undefined 
                          ? `${result.training_metrics.test_mae_pct.toFixed(2)}%`
                          : result.training_metrics.test_mae !== undefined
                          ? `₹${result.training_metrics.test_mae.toFixed(2)}`
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Test RMSE</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {result.training_metrics.test_rmse_pct !== undefined 
                          ? `${result.training_metrics.test_rmse_pct.toFixed(2)}%`
                          : result.training_metrics.test_rmse !== undefined
                          ? `₹${result.training_metrics.test_rmse.toFixed(2)}`
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Features Used</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {result.training_metrics.feature_count}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Top Features */}
              {result.prediction && result.prediction.top_features && result.prediction.top_features.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Top Contributing Features</h3>
                  <div className="space-y-2">
                    {result.prediction.top_features.slice(0, 5).map((feature, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{feature.name}</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {(feature.importance * 100).toFixed(2)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Disclaimer */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Disclaimer:</strong> This prediction is based on historical data and machine learning models. 
                Stock market predictions are inherently uncertain and should not be considered as financial advice. 
                Always conduct your own research and consult with a financial advisor before making investment decisions.
              </p>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default UniversalPricePredictionPage;

